import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api, { postEmailForVerification } from "../utils/api";
import { UIUtils } from "../utils/uiUtils";
import DateInput from "../components/DateInput";
import { subYears, format } from "date-fns";
import { Button } from "../components/Button";
import config from "../configs/config";
import { useBottomNavBar } from "../contexts/BottomNavBarContext";
import { Resources } from "../utils/resources";
import { validateRedirectUrl } from "../utils/routeUtils";
import { setCookie } from "../utils/cookieUtils";
import { isValidIntent, Intent } from "../utils/types/intent";
import PageMetadata from "../components/PageMetadata";
import desktopBlurred from "../assets/desktop-blurred.png";
import mobileBlurred from "../assets/mobile-blurred.png";
import {
  CATEGORY_OPTION_MAP,
  CategoryOption,
  OTHER_OPTION_LABEL,
  OTHER_GOAL_PREFIX,
  extractOtherGoalText,
  isOtherGoalString,
  matchCategoryOption,
} from "../utils/categoryOptions";

interface LocationState {
  email?: string;
  fromLogin?: boolean;
  intent?: string;
  leadDetails?: {
    first_name?: string;
    last_name?: string;
    email?: string;
    occupancy?: string;
    monthly_income?: string;
    dob?: Date | null;
    marital_status?: string;
    no_of_children?: string;
    // Add other fields that match signup form
  };
  fromExtLanding?: boolean;
  goal?: string;
}

type Step = "welcome" | "account" | "goals" | "details";

interface UserData {
  firstName: string;
  lastName: string;
  email: string;
  dateOfBirth: Date | null;
  goal: string;
  maritalStatus: string;
  numberOfChildren: string;
  occupancy: string;
  monthlyIncome: string;
  agreedToPrivacyPolicy: boolean;
  receiveUpdates: boolean;
  receiveSmsUpdates: boolean;
}

interface StepSection {
  content: React.ReactNode;
  heading?: string;
  description?: string;
}

const Welcome: React.FC = () => {
  const location = useLocation();
  const state = location.state as LocationState;
  const navigate = useNavigate();
  const { setIsVisible } = useBottomNavBar();
  const [currentStep, setCurrentStep] = useState<Step>("welcome");
  const [isLoading, setIsLoading] = useState(false);
  const heroBackgrounds = {
    desktop: desktopBlurred,
    mobile: mobileBlurred,
  };
  const baseFormInputClass =
    "form-input w-full rounded-2xl border border-black/10 bg-white/90 text-base placeholder:text-gray-500 focus:border-black focus:ring-0";

  const [isDesktopHero, setIsDesktopHero] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }
    return window.matchMedia("(min-width: 1024px)").matches;
  });

  const getButtonLabel = () => {
    if (currentStep === "welcome") {
      return "Unlock free tips";
    }
    if (currentStep === "details") {
      return "Continue";
    }
    return "Next";
  };

  // Refs for focus management
  const emailInputRef = useRef<HTMLInputElement>(null);
  const firstNameRef = useRef<HTMLInputElement>(null);
  const goalTextareaRef = useRef<HTMLTextAreaElement>(null);
  const maritalStatusRef = useRef<HTMLSelectElement>(null);

  // Focus management when step changes
  useEffect(() => {
    switch (currentStep) {
      case "welcome":
        emailInputRef.current?.focus();
        break;
      case "account":
        firstNameRef.current?.focus();
        break;
      case "goals":
        goalTextareaRef.current?.focus();
        break;
      case "details":
        maritalStatusRef.current?.focus();
        break;
    }
  }, [currentStep]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const mediaQuery = window.matchMedia("(min-width: 1024px)");
    const handleChange = (event: MediaQueryListEvent) =>
      setIsDesktopHero(event.matches);

    // Set initial state in case the default constructor ran on the server
    setIsDesktopHero(mediaQuery.matches);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
    } else {
      mediaQuery.addListener(handleChange);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  // Get and validate redirectTo from URL params
  const params = new URLSearchParams(location.search);
  const redirectTo = validateRedirectUrl(params.get("redirectTo"));
  const directApply = params.get("dap");
  const intent = params.get("intent") || "";
  const rawCategoryParam = (params.get("category") || "").trim();
  const rawSubCategoryParam = (params.get("subCategory") || "").trim();
  const normalizedCategoryParam = rawCategoryParam.toLowerCase();
  const categoryFromMap = normalizedCategoryParam
    ? CATEGORY_OPTION_MAP[normalizedCategoryParam]
    : undefined;
  const categoryContent = categoryFromMap?.options?.length
    ? categoryFromMap
    : undefined;
  const isCategoryFlowActive = Boolean(categoryContent);

  const [hasConfirmedCategoryGoal, setHasConfirmedCategoryGoal] = useState(
    () => !isCategoryFlowActive,
  );
  const [selectedCategoryOption, setSelectedCategoryOption] = useState(
    () => matchCategoryOption(categoryContent, state?.goal)?.label || "",
  );
  const [otherCategoryText, setOtherCategoryText] = useState(() =>
    extractOtherGoalText(state?.goal),
  );

  const [userData, setUserData] = useState<UserData>({
    firstName: state?.leadDetails?.first_name || "",
    lastName: state?.leadDetails?.last_name || "",
    email: state?.leadDetails?.email || state?.email || "",
    dateOfBirth: state?.leadDetails?.dob || null,
    goal: state?.goal || "",
    maritalStatus: state?.leadDetails?.marital_status || "",
    numberOfChildren: state?.leadDetails?.no_of_children || "",
    occupancy: state?.leadDetails?.occupancy || "",
    monthlyIncome: state?.leadDetails?.monthly_income || "",
    agreedToPrivacyPolicy: false,
    receiveUpdates: false,
    receiveSmsUpdates: false,
  });
  // Currently logic is not needed
  // const heroHeadingText = categoryFromMap
  //   ? categoryFromMap.heading_signup
  //   : userData.goal
  //   ? "Afforda is here to help you with what you need, clearly and for free."
  //   : "Afforda is here to help you with what you need, clearly and for free.";
  const heroHeadingText =
    "Afforda is here to help you with what you need, clearly and for free.";

  useEffect(() => {
    setIsVisible(false);
    // Store direct apply parameter in cookie if present
    if (directApply === "x29") {
      setCookie("afforda_direct_apply", "true");
    }
    return () => setIsVisible(true);
  }, [setIsVisible, directApply]);

  useEffect(() => {
    const nextValue = !isCategoryFlowActive;
    setHasConfirmedCategoryGoal((prev) =>
      prev === nextValue ? prev : nextValue,
    );
  }, [isCategoryFlowActive]);

  useEffect(() => {
    if (isCategoryFlowActive && userData.goal && !selectedCategoryOption) {
      const matchedOption = matchCategoryOption(categoryContent, userData.goal);
      if (matchedOption) {
        setSelectedCategoryOption(matchedOption.label);
        if (matchedOption.label === OTHER_OPTION_LABEL) {
          const extractedText = extractOtherGoalText(userData.goal);
          if (extractedText) {
            setOtherCategoryText(extractedText);
          }
        }
      }
    }
  }, [
    categoryContent,
    isCategoryFlowActive,
    selectedCategoryOption,
    userData.goal,
  ]);

  useEffect(() => {
    if (!categoryContent) {
      setSelectedCategoryOption("");
      setOtherCategoryText("");
      return;
    }
    setSelectedCategoryOption((prev) => {
      const matchedOption = matchCategoryOption(categoryContent, prev);
      if (!matchedOption) {
        setOtherCategoryText("");
        return "";
      }
      return matchedOption.label;
    });
  }, [categoryContent]);

  useEffect(() => {
    if (selectedCategoryOption !== OTHER_OPTION_LABEL) {
      return;
    }
    const extractedText = extractOtherGoalText(userData.goal);
    if (!extractedText) {
      return;
    }
    setOtherCategoryText((prev) =>
      prev === extractedText ? prev : extractedText,
    );
  }, [selectedCategoryOption, userData.goal]);

  // Sanitize and validate goal text
  const sanitizeGoal = (text: string): string => {
    // Remove any HTML tags and decode entities to prevent XSS
    const div = document.createElement("div");
    div.textContent = text;
    const sanitized = div.textContent || "";

    // Truncate to max length
    const MAX_GOAL_LENGTH = 250;
    return sanitized.slice(0, MAX_GOAL_LENGTH);
  };

  const buildOtherGoal = (text: string): string => {
    const trimmedText = text.trim();
    if (!trimmedText) {
      return "";
    }
    return sanitizeGoal(`${OTHER_GOAL_PREFIX}${trimmedText}`);
  };

  useEffect(() => {
    if (isCategoryFlowActive) {
      return;
    }
    const fallbackGoal = rawSubCategoryParam || rawCategoryParam;
    if (!fallbackGoal) {
      return;
    }
    const sanitizedCategoryGoal = sanitizeGoal(fallbackGoal);
    setUserData((prev) => {
      if (prev.goal === sanitizedCategoryGoal) {
        return prev;
      }
      return {
        ...prev,
        goal: sanitizedCategoryGoal,
      };
    });
  }, [isCategoryFlowActive, rawCategoryParam, rawSubCategoryParam]);

  const getGoalFromCategoryQueries = () => {
    if (selectedCategoryOption) {
      if (
        selectedCategoryOption === OTHER_OPTION_LABEL &&
        otherCategoryText.trim()
      ) {
        return buildOtherGoal(otherCategoryText.trim());
      }
      const matchedOption = matchCategoryOption(
        categoryContent,
        selectedCategoryOption,
      );
      if (matchedOption) {
        return matchedOption.goal;
      }
    }
    const fallbackGoal = rawSubCategoryParam || rawCategoryParam;
    if (!fallbackGoal) {
      return "";
    }
    if (isOtherGoalString(fallbackGoal)) {
      const extractedText = extractOtherGoalText(fallbackGoal);
      return extractedText ? buildOtherGoal(extractedText) : "";
    }
    const matchedOption = matchCategoryOption(categoryContent, fallbackGoal);
    if (matchedOption) {
      if (
        matchedOption.label === OTHER_OPTION_LABEL &&
        otherCategoryText.trim()
      ) {
        return buildOtherGoal(otherCategoryText.trim());
      }
      return matchedOption.goal;
    }
    return sanitizeGoal(fallbackGoal);
  };

  const handleInputChange = (field: keyof UserData, value: any) => {
    // Apply sanitization for goal field
    const finalValue = field === "goal" ? sanitizeGoal(value) : value;
    setUserData((prev) => ({
      ...prev,
      [field]: finalValue,
    }));
  };

  const handleOtherCategoryInput = (
    value: string,
    { forceGoalUpdate = false }: { forceGoalUpdate?: boolean } = {},
  ) => {
    setOtherCategoryText(value);
    const shouldUpdateGoal =
      forceGoalUpdate || selectedCategoryOption === OTHER_OPTION_LABEL;
    if (!shouldUpdateGoal) {
      return;
    }
    const trimmedValue = value.trim();
    handleInputChange("goal", trimmedValue ? buildOtherGoal(trimmedValue) : "");
  };

  const handleCategorySelection = (option: CategoryOption) => {
    setSelectedCategoryOption(option.label);
    if (option.label === OTHER_OPTION_LABEL) {
      handleOtherCategoryInput(otherCategoryText, { forceGoalUpdate: true });
      return;
    }
    handleInputChange("goal", option.goal);
  };

  const handleCategoryContinue = () => {
    if (!selectedCategoryOption) {
      UIUtils.showMessageOnSnackBar("Please pick an option to continue");
      return;
    }
    if (
      selectedCategoryOption === OTHER_OPTION_LABEL &&
      !otherCategoryText.trim()
    ) {
      UIUtils.showMessageOnSnackBar("Please tell us what needs fixing");
      return;
    }
    setHasConfirmedCategoryGoal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (currentStep === "welcome") {
      const trimmedEmail = userData.email.trim();
      if (!trimmedEmail) {
        UIUtils.showMessageOnSnackBar("Please enter your email address");
        return;
      }

      setIsLoading(true);
      try {
        // Try to login with email
        const response = await postEmailForVerification(trimmedEmail, intent);

        if (response.data.status === "USER_NOT_FOUND") {
          // Email doesn't exist, proceed to signup
          handleInputChange("email", trimmedEmail);
          setCurrentStep("account");
        } else if (response.data.reference_id) {
          // Email exists and verification code sent
          // Check for goal from different sources
          let goal = userData.goal;
          if (!goal) {
            const userInput = params.get("userInput");
            if (userInput) {
              try {
                goal = decodeURIComponent(userInput);
              } catch (error) {
                console.error("Error decoding goal input:", error);
              }
            }
          }

          if (!goal) {
            goal = getGoalFromCategoryQueries();
          }

          const navigationState = {
            from: redirectTo,
            intent: isValidIntent(intent) ? (intent as Intent) : undefined,
            directApply: directApply === "x29",
            goal: goal || undefined,
            heroHeading: heroHeadingText,
          };
          navigate(`/email-verification/${response.data.reference_id}`, {
            state: navigationState,
          });
        } else {
          throw new Error("Invalid response from server");
        }
      } catch (error: any) {
        UIUtils.showMessageOnSnackBar(
          error.response?.data?.message ||
            "An error occurred. Please try again.",
        );
      } finally {
        setIsLoading(false);
      }
      return;
    }

    if (currentStep === "account") {
      if (!userData.dateOfBirth) {
        UIUtils.showMessageOnSnackBar("Please enter your date of birth");
        return;
      }

      // Check for goal from different sources
      let hasGoal = Boolean(userData.goal);

      // Check URL parameter if no goal yet
      if (!hasGoal) {
        const userInput = params.get("userInput");
        if (userInput) {
          try {
            const decodedInput = decodeURIComponent(userInput);
            handleInputChange("goal", decodedInput);
            hasGoal = true;
          } catch (error) {
            console.error("Error decoding goal input:", error);
          }
        }
      }

      if (!hasGoal) {
        const categoryGoal = getGoalFromCategoryQueries();
        if (categoryGoal) {
          handleInputChange("goal", categoryGoal);
          hasGoal = true;
        }
      }

      // Skip goals step if we already have a goal
      setCurrentStep(hasGoal ? "details" : "goals");
      return;
    }

    if (currentStep === "goals") {
      if (!userData.goal) {
        UIUtils.showMessageOnSnackBar(
          "Please describe your financial challenge or goal",
        );
        return;
      }
      setCurrentStep("details");
      return;
    }
    setIsLoading(true);
    try {
      const signUpData: any = {
        first_name: userData.firstName,
        email: userData.email,
        dob: format(userData.dateOfBirth!, "yyyy-MM-dd"),
        privacy_consent: userData.agreedToPrivacyPolicy,
        marketing_email_dnc_flag: !userData.receiveUpdates,
        marketing_sms_dnc_flag: !userData.receiveSmsUpdates,
        created_by: "web-app",
        intent: params.get("intent") || "",
        additional_data: {
          type_of_housing: userData.occupancy || "",
          childrenNum: parseInt(userData.numberOfChildren) || 0,
          marital_status: userData.maritalStatus || "",
          income_amount: parseInt(userData.monthlyIncome) || 0,
          user_goal: userData.goal || "",
        },
      };

      const response = await api.signUpUser(signUpData);

      if (response.data.reference_id) {
        const navigationState = {
          from: redirectTo,
          intent: isValidIntent(intent) ? (intent as Intent) : undefined,
          directApply: directApply === "x29",
          goal: userData.goal || undefined,
          heroHeading: heroHeadingText,
        };
        navigate(`/email-verification/${response.data.reference_id}`, {
          state: navigationState,
        });
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (error: any) {
      UIUtils.showMessageOnSnackBar(
        error.response?.data?.message ||
          "Oops! Sign-up failed. Please try again later.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const renderHeroScaffold = ({
    cardContent,
    heading = heroHeadingText,
    withBackground = true,
    cardWidth,
    cardClasses,
    showBackLink = true,
  }: {
    cardContent: React.ReactNode;
    heading?: string;
    withBackground?: boolean;
    cardWidth?: number;
    cardClasses?: string;
    showBackLink?: boolean;
  }) => {
    const defaultCardClasses = "border border-black bg-white p-6 sm:p-8";
    const resolvedCardClasses = cardClasses || defaultCardClasses;

    const backgroundStyles = withBackground
      ? {
          backgroundImage: `url(${isDesktopHero ? heroBackgrounds.desktop : heroBackgrounds.mobile})`,
          backgroundSize: "cover",
          backgroundPosition: "center top",
          backgroundRepeat: "no-repeat",
          width: isDesktopHero ? "740px" : "100%",
          maxWidth: isDesktopHero ? undefined : "500px",
          borderRadius: "48px",
          padding: isDesktopHero ? "80px 40px" : "40px 16px",
          minHeight: isDesktopHero ? "500px" : "440px",
          display: "flex",
          justifyContent: "center",
        }
      : undefined;

    const cardWrapperStyles: React.CSSProperties = {
      width: "100%",
      maxWidth: cardWidth
        ? `${cardWidth}px`
        : isDesktopHero
          ? "450px"
          : "380px",
    };

    return (
      <div className="mx-auto flex w-full max-w-[900px] flex-col items-center px-0 pb-12 sm:px-6 lg:pb-16">
        <section className="mt-10 w-full px-0 sm:px-4">
          {heading && (
            <h2 className="mb-6 text-3xl font-semibold leading-tight text-gray-900 lg:text-4xl">
              {heading}
            </h2>
          )}
          <div className="flex justify-center">
            <div
              className={`flex w-full justify-center ${withBackground ? "bg-cover bg-center" : ""}`}
              style={backgroundStyles}
            >
              <div className="w-full" style={cardWrapperStyles}>
                <div
                  className={resolvedCardClasses}
                  style={{
                    borderRadius: "38px",
                  }}
                >
                  {cardContent}
                </div>
              </div>
            </div>
          </div>
        </section>
        {showBackLink && (
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="mt-8 flex items-center gap-2 text-sm font-semibold text-gray-700 hover:text-black"
          >
            <span aria-hidden="true">&larr;</span> Go back
          </button>
        )}
      </div>
    );
  };

  const renderCategoryStep = () => {
    if (!categoryContent) {
      return null;
    }

    const isOtherSelected = selectedCategoryOption === OTHER_OPTION_LABEL;
    const hasOtherCategoryInput = Boolean(otherCategoryText.trim());
    const isContinueDisabled =
      !selectedCategoryOption || (isOtherSelected && !hasOtherCategoryInput);
    const otherInputId = "category-other-input";

    return renderHeroScaffold({
      cardContent: (
        <div className="space-y-8 text-center lg:text-left">
          <div>
            <h2 className="text-4xl font-black text-gray-900 text-left">
              {categoryContent.heading}
            </h2>
            {/* <p className="mt-2 text-base text-gray-600">
              Tap the option that best describes what's going on right now.
            </p> */}
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {categoryContent.options.map((option) => {
              const isSelected = selectedCategoryOption === option.label;
              return (
                <button
                  key={option.label}
                  type="button"
                  onClick={() => handleCategorySelection(option)}
                  aria-pressed={isSelected}
                  className={`flex items-center gap-3 rounded-2xl border px-4 py-3 text-left font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-black ${
                    isSelected
                      ? "border-black bg-white shadow-lg"
                      : "border-black/10 bg-white hover:border-black/40"
                  }`}
                >
                  <span
                    className={`flex h-5 w-5 items-center justify-center rounded-full border ${
                      isSelected ? "border-black" : "border-gray-400"
                    }`}
                  >
                    {isSelected && (
                      <span className="h-2.5 w-2.5 rounded-full bg-black" />
                    )}
                  </span>
                  <span className="text-base font-semibold text-gray-900">
                    {option.label}
                  </span>
                </button>
              );
            })}
          </div>

          {isOtherSelected && (
            <div className="text-left">
              <label
                htmlFor={otherInputId}
                className="text-sm font-semibold text-gray-800"
              >
                Tell us what needs fixing
              </label>
              <input
                id={otherInputId}
                type="text"
                value={otherCategoryText}
                onChange={(e) => handleOtherCategoryInput(e.target.value)}
                className={`${baseFormInputClass} mt-2`}
                placeholder="Eg: Shower, sink, dryer"
                maxLength={120}
              />
              <p className="mt-1 text-xs text-gray-500">
                We'll turn this into your goal so we can help you better.
              </p>
            </div>
          )}

          <div className="pt-2">
            <Button
              text="Continue"
              type="button"
              onClick={handleCategoryContinue}
              disabled={isContinueDisabled}
              className={`w-full rounded-full bg-black py-3 text-base font-semibold ${
                isContinueDisabled ? "opacity-40" : ""
              }`}
            />
          </div>
        </div>
      ),
      heading: "",
      cardWidth: 640,
      withBackground: false,
      cardClasses: "p-6 sm:p-8",
      showBackLink: false,
    });
  };

  const renderWelcomeStep = () =>
    renderHeroScaffold({
      cardContent: (
        <>
          <p className="text-xl font-semibold text-gray-900">Sign Up</p>
          <div className="mt-4 space-y-4">
            <label className="sr-only" htmlFor="email">
              Email
            </label>
            <input
              ref={emailInputRef}
              data-sensitive="true"
              type="email"
              id="email"
              name="email"
              value={userData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              required
              className="form-input w-full border border-black/10 bg-gray-100/80 text-base"
              style={{ borderRadius: "10px" }}
              placeholder="Enter your email"
              disabled={isLoading}
              autoComplete="email"
              aria-label="Email"
            />
            <p className="text-xs text-gray-500">
              We'll send a secure login code here.
            </p>
            <Button
              text={getButtonLabel()}
              onClick={() => {}}
              type="submit"
              className="w-full rounded-full bg-black py-3 text-base font-semibold"
              disabled={isLoading}
            />
          </div>
        </>
      ),
      withBackground: Boolean(userData.goal),
    });

  const renderAccountStep = (): StepSection => ({
    heading: "Create an account",
    description:
      "Takes seconds. No passwords. Just real help, tailored to you.",
    content: (
      <div className="space-y-6">
        <div>
          <input
            ref={firstNameRef}
            type="text"
            id="firstName"
            value={userData.firstName}
            onChange={(e) => handleInputChange("firstName", e.target.value)}
            required
            className={baseFormInputClass}
            placeholder="First name*"
            aria-label="First name"
          />
        </div>
        <div>
          <input
            type="text"
            id="lastName"
            value={userData.lastName}
            onChange={(e) => handleInputChange("lastName", e.target.value)}
            className={baseFormInputClass}
            placeholder="Last name"
            aria-label="Last name"
          />
        </div>
        <div>
          <DateInput
            dataSensitive="true"
            selected={userData.dateOfBirth}
            onChange={(date) => handleInputChange("dateOfBirth", date)}
            name="dob"
            maxDate={subYears(new Date(), 18)}
            placeholderText="Date of birth (DD/MM/YYYY)*"
            showYearDropdown
            scrollableYearDropdown
            yearDropdownItemNumber={100}
            className={baseFormInputClass}
            dateFormat="dd/MM/yyyy"
            aria-label="Date of birth"
          />
        </div>
        <div className="space-y-4 rounded-2xl bg-white/70 p-4">
          {/* Marketing Consent Section - Grouped */}
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
            {/* Marketing Consent Info */}
            <div className="mb-3 flex items-start gap-2">
              <svg
                className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600"
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
              <div className="text-sm">
                <strong className="text-secondary">Marketing communications from Afforda</strong>
                <p className="mt-1 text-xs text-gray-600">
                  Gain Credit has a range of products and brands. These include Afforda. We want to share news and deals from all of them. We may also share offers from some brands we trust. You can unsubscribe at any time in your profile settings. Select how you'd like to hear from us:
                </p>
              </div>
            </div>

            {/* Email and SMS Consent Checkboxes */}
            <div className="space-y-3">
            <div className="flex items-start">
              <div className="flex h-5 items-center">
                <input
                  type="checkbox"
                  id="receiveUpdates"
                  checked={userData.receiveUpdates}
                  onChange={(e) =>
                    handleInputChange("receiveUpdates", e.target.checked)
                  }
                  className="form-checkbox"
                />
              </div>
              <label
                htmlFor="receiveUpdates"
                className="ml-2 text-sm text-secondary"
              >
                Email
              </label>
            </div>

            <div className="flex items-start">
              <div className="flex h-5 items-center">
                <input
                  type="checkbox"
                  id="receiveSmsUpdates"
                  checked={userData.receiveSmsUpdates}
                  onChange={(e) =>
                    handleInputChange("receiveSmsUpdates", e.target.checked)
                  }
                  className="form-checkbox"
                />
              </div>
              <label
                htmlFor="receiveSmsUpdates"
                className="ml-2 text-sm text-secondary"
              >
                SMS
              </label>
            </div>
          </div>
          </div>

          <div className="flex items-start">
            <div className="flex h-5 items-center">
              <input
                type="checkbox"
                id="agreedToPrivacyPolicy"
                required
                checked={userData.agreedToPrivacyPolicy}
                onChange={(e) =>
                  handleInputChange("agreedToPrivacyPolicy", e.target.checked)
                }
                className="form-checkbox"
              />
            </div>
            <label
              htmlFor="agreedToPrivacyPolicy"
              className="ml-2 text-sm text-secondary"
            >
              I agree to the{" "}
              <a
                href={`${config.AFFORDA_STATIC_SITE}/terms-of-use`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline"
              >
                Terms of Use
              </a>{" "}
              and have read the{" "}
              <a
                href={`${config.AFFORDA_STATIC_SITE}/privacy-policy`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline"
              >
                Privacy Policy
              </a>
              , which explains how my personal information will be processed and
              shared. This includes any sensitive information I may choose to
              provide, such as information about my health, and I'm aware that I
              can withdraw this consent at any time.
            </label>
          </div>
        </div>
      </div>
    ),
  });

  const renderGoalsStep = (): StepSection => ({
    heading: "Tell us what we can help with",
    description:
      "We ask because it'll help us give you a better Afforda experience. The more details you share, the more relevant help we can surface.",
    content: (
      <div>
        <textarea
          ref={goalTextareaRef}
          value={userData.goal}
          onChange={(e) => handleInputChange("goal", e.target.value)}
          placeholder="Tell us about your financial challenge or goal"
          className={`${baseFormInputClass} h-32`}
          required
          maxLength={250}
        />
      </div>
    ),
  });

  const renderDetailsStep = (): StepSection => ({
    heading: "What's your current situation?",
    description:
      "No judgment - we just want to check if you're missing out on anything you could get.",
    content: (
      <div className="space-y-6">
        <div>
          <select
            ref={maritalStatusRef}
            value={userData.maritalStatus}
            onChange={(e) => handleInputChange("maritalStatus", e.target.value)}
            className={baseFormInputClass}
            aria-label="Marital status"
          >
            <option value="">Marital status</option>
            {Object.entries(Resources.maritalStatusOptions).map(
              ([label, value]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ),
            )}
          </select>
        </div>
        <div>
          <select
            value={userData.numberOfChildren}
            onChange={(e) =>
              handleInputChange("numberOfChildren", e.target.value)
            }
            className={baseFormInputClass}
            aria-label="Number of children"
          >
            <option value="">Number of children</option>
            {Object.entries(Resources.numberOfChildrenOptions).map(
              ([label, value]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ),
            )}
          </select>
        </div>
        <div>
          <select
            value={userData.occupancy}
            onChange={(e) => handleInputChange("occupancy", e.target.value)}
            className={baseFormInputClass}
            aria-label="Type of occupancy"
          >
            <option value="">Type of occupancy</option>
            {Object.entries(Resources.housingTypeOptions).map(
              ([label, value]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ),
            )}
          </select>
        </div>
        <div>
          <div className="relative">
            <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-base text-gray-500">
              £
            </span>
            <input
              type="number"
              value={userData.monthlyIncome}
              onChange={(e) => {
                const value = e.target.value;
                if (!value || (Number(value) >= 0 && Number(value) <= 15000)) {
                  handleInputChange("monthlyIncome", value);
                }
              }}
              className={`${baseFormInputClass} pl-10`}
              min="0"
              max="15000"
              placeholder="Monthly income (after tax)"
              aria-label="Monthly income"
            />
          </div>
        </div>
        <p className="text-sm text-secondary">
          Or you can continue now and fill it out later.
        </p>
      </div>
    ),
  });

  const renderMultiStepForm = () => {
    const progressPercent =
      currentStep === "account" ? 33 : currentStep === "goals" ? 66 : 100;

    const handleBackNavigation = () => {
      if (currentStep === "account") {
        setCurrentStep("welcome");
      } else if (currentStep === "goals") {
        setCurrentStep("account");
      } else if (currentStep === "details") {
        if (userData.goal) {
          setCurrentStep("account");
        } else {
          setCurrentStep("goals");
        }
      }
    };

    const stepSection =
      currentStep === "account"
        ? renderAccountStep()
        : currentStep === "goals"
          ? renderGoalsStep()
          : renderDetailsStep();

    const headingCopy = stepSection.heading || "Create an account";
    const descriptionCopy =
      stepSection.description ||
      "Takes seconds. No passwords. Just real help, tailored to you.";

    return (
      <form onSubmit={handleSubmit} className="mx-auto w-full space-y-6">
        <div className="space-y-4">
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.1)]">
            <div
              className="h-full rounded-full bg-black transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <div>
            <h1 className="mt-2 text-3xl font-black text-gray-900">
              {headingCopy}
            </h1>
            <p className="mt-2 text-base text-gray-600">{descriptionCopy}</p>
          </div>
        </div>

        <div className="space-y-6">{stepSection.content}</div>

        <div className="pt-2">
          <Button
            text={getButtonLabel()}
            onClick={() => {}}
            type="submit"
            className="w-full rounded-full bg-black py-3 text-lg font-semibold"
            disabled={isLoading}
          />
        </div>

        <button
          type="button"
          onClick={handleBackNavigation}
          className="flex items-center gap-2 text-sm font-semibold text-gray-600 transition hover:text-black"
        >
          <span aria-hidden="true">&larr;</span> Go back
        </button>
      </form>
    );
  };

  const shouldShowCategoryStep = Boolean(
    categoryContent && !hasConfirmedCategoryGoal,
  );

  return (
    <div className="max-w-[1360px]">
      <PageMetadata
        title="Afforda | Credit, Savings, & Benefits"
        description="Afforda - Your Financial Wellbeing Partner"
        canonicalUrl={`${config.AFFORDA_APP_URL}/`}
      />
      {isLoading ? (
        <div className="text-center">
          {currentStep === "details"
            ? "Setting up your account..."
            : "Please wait..."}
        </div>
      ) : currentStep === "welcome" ? (
        shouldShowCategoryStep ? (
          renderCategoryStep()
        ) : (
          <form onSubmit={handleSubmit} className="w-full">
            {renderWelcomeStep()}
          </form>
        )
      ) : (
        renderHeroScaffold({
          cardContent: renderMultiStepForm(),
          withBackground: false,
          cardWidth: 540,
          cardClasses: "p-8 sm:p-10 border-black/10",
          heading: "",
          showBackLink: false,
        })
      )}
    </div>
  );
};

export default Welcome;
